import type {
  ResourceChangeRealtimeConnectOptions,
  ResourceChangeRealtimeHandlers,
} from "./resource-change-realtime.types";
import type {QueryClient, QueryKey} from "@tanstack/react-query";

import {RealtimeResourceChange, RealtimeStreamReady} from "@project/sdk/realtime";
import {ErrorEvent, EventSource} from "eventsource";

const REALTIME_URL = "/api/v1/realtime";
const READY_BASELINE_TIMEOUT_MS = 10_000;
const RETRY_BASE_MS = 1_000;
const RETRY_MAX_MS = 30_000;
const RETRY_JITTER_MS = 250;

export class ResourceChangeRealtimeRouter {
  private source: EventSource | null = null;
  private started = false;
  private forbidden = false;
  private generation = 0;
  private queue = Promise.resolve();
  private readyResources: ReadonlySet<string> | null = null;
  private retryAttempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private readyTimer: ReturnType<typeof setTimeout> | null = null;
  private connectOptions: ResourceChangeRealtimeConnectOptions = {};

  constructor(
    private readonly queryClient: QueryClient,
    private readonly handlers: ResourceChangeRealtimeHandlers,
  ) {
    for (const [resource, handler] of Object.entries(handlers)) {
      if (resource !== handler.resource) {
        throw new Error(`Realtime handler registry mismatch: ${resource}`);
      }
    }
  }

  public connect(options: ResourceChangeRealtimeConnectOptions = {}) {
    this.connectOptions = options;
    if (this.started || this.forbidden) return;

    this.started = true;
    this.openSource();
  }

  public disconnect() {
    this.started = false;
    this.forbidden = false;
    this.generation += 1;
    this.clearTimers();
    this.source?.close();
    this.source = null;
    this.readyResources = null;
    this.queue = Promise.resolve();
    this.removeAllRegisteredQueries();
  }

  private openSource() {
    if (!this.started || this.forbidden) return;

    const generation = ++this.generation;
    const source = new EventSource(REALTIME_URL);

    this.source = source;
    this.readyResources = null;
    this.queue = Promise.resolve();

    source.addEventListener("open", () => {
      this.enqueue(generation, source, async () => {
        this.readyResources = null;
        this.startReadyDeadline(generation, source);
      });
    });

    source.addEventListener("stream-ready.v1", (event) => {
      this.enqueue(generation, source, () => this.receiveReady(event, generation, source));
    });

    source.addEventListener("resource-change.v1", (event) => {
      this.enqueue(generation, source, () => this.receiveChange(event, generation, source));
    });

    source.addEventListener("error", (event) => {
      const readyState = source.readyState;

      this.enqueue(generation, source, () =>
        this.receiveError(event, readyState, generation, source),
      );
    });
  }

  private enqueue(generation: number, source: EventSource, work: () => Promise<void>) {
    this.queue = this.queue
      .then(async () => {
        if (this.isCurrent(generation, source)) await work();
      })
      .catch(async () => {
        if (this.isCurrent(generation, source)) {
          await this.failAndReplace(generation, source);
        }
      });
  }

  private async receiveReady(event: MessageEvent, generation: number, source: EventSource) {
    const ready = this.parse(RealtimeStreamReady, event.data);
    const resources = ready ? new Set(ready.resources) : null;

    if (!ready || resources?.size !== ready.resources.length) {
      await this.failAndReplace(generation, source);

      return;
    }

    this.clearReadyTimer();

    for (const handler of Object.values(this.handlers)) {
      if (!this.isCurrent(generation, source)) return;

      if (!resources.has(handler.resource)) {
        this.queryClient.removeQueries({queryKey: handler.queryKey});
        continue;
      }

      const recovered = await this.cancelAndInvalidate(handler.queryKey, generation, source);

      if (!recovered) return;
    }

    if (!this.isCurrent(generation, source)) return;
    this.readyResources = resources;
    this.retryAttempt = 0;
  }

  private async receiveChange(event: MessageEvent, generation: number, source: EventSource) {
    const genericChange = this.parse(RealtimeResourceChange, event.data);

    if (!genericChange) {
      await this.failAndReplace(generation, source);

      return;
    }

    const handler = this.handlers[genericChange.resource];

    if (!handler) return;

    if (!this.readyResources?.has(handler.resource)) {
      await this.failAndReplace(generation, source);

      return;
    }

    const exactChange = handler.eventSchema.safeParse(genericChange);

    if (!exactChange.success) {
      await this.failAndReplace(generation, source);

      return;
    }

    await handler.handleEvent(exactChange.data, this.queryClient);
  }

  private async receiveError(
    event: Event,
    readyStateAtDispatch: number,
    generation: number,
    source: EventSource,
  ) {
    const error = event instanceof ErrorEvent ? event : undefined;
    const status = error?.code;

    if (status === 403) {
      this.removeAllRegisteredQueries();
      this.forbidden = true;
      this.closeGeneration(generation, source);

      return;
    }

    if (status === 401) {
      this.removeAllRegisteredQueries();
      if (error) this.connectOptions.onAuthenticationRisk?.(error);
      this.replaceGeneration(generation, source);

      return;
    }

    if (readyStateAtDispatch === EventSource.CONNECTING) {
      this.readyResources = null;
      this.clearReadyTimer();
      await this.recoverAllRegisteredQueries(generation, source);
      if (status === undefined && error && this.isCurrent(generation, source)) {
        this.connectOptions.onAuthenticationRisk?.(error);
      }

      return;
    }

    await this.recoverAllRegisteredQueries(generation, source);
    if (status === undefined && error && this.isCurrent(generation, source)) {
      this.connectOptions.onAuthenticationRisk?.(error);
    }
    this.replaceGeneration(generation, source);
  }

  private startReadyDeadline(generation: number, source: EventSource) {
    this.clearReadyTimer();
    this.readyTimer = setTimeout(() => {
      this.enqueue(generation, source, async () => {
        if (!this.isCurrent(generation, source) || this.readyResources !== null) return;
        await this.failAndReplace(generation, source);
      });
    }, READY_BASELINE_TIMEOUT_MS);
  }

  private async failAndReplace(generation: number, source: EventSource) {
    try {
      await this.recoverAllRegisteredQueries(generation, source);
    } finally {
      if (this.isCurrent(generation, source)) {
        this.replaceGeneration(generation, source);
      }
    }
  }

  private async recoverAllRegisteredQueries(generation: number, source: EventSource) {
    for (const {queryKey} of Object.values(this.handlers)) {
      const recovered = await this.cancelAndInvalidate(queryKey, generation, source);

      if (!recovered) return false;
    }

    return this.isCurrent(generation, source);
  }

  private async cancelAndInvalidate(queryKey: QueryKey, generation: number, source: EventSource) {
    if (!this.isCurrent(generation, source)) return false;
    await this.queryClient.cancelQueries({queryKey});
    if (!this.isCurrent(generation, source)) return false;
    await this.queryClient.invalidateQueries({queryKey});

    return this.isCurrent(generation, source);
  }

  private removeAllRegisteredQueries() {
    for (const {queryKey} of Object.values(this.handlers)) {
      this.queryClient.removeQueries({queryKey});
    }
  }

  private replaceGeneration(generation: number, source: EventSource) {
    const replacementToken = this.closeGeneration(generation, source);

    if (replacementToken === null || !this.started || this.forbidden) return;

    const exponential = RETRY_BASE_MS * 2 ** this.retryAttempt++;
    const jitter = Math.floor(Math.random() * RETRY_JITTER_MS);
    const delay = Math.min(RETRY_MAX_MS, exponential + jitter);

    this.retryTimer = setTimeout(() => {
      if (!this.started || this.forbidden || this.generation !== replacementToken) return;
      this.retryTimer = null;
      this.openSource();
    }, delay);
  }

  private closeGeneration(generation: number, source: EventSource) {
    if (!this.isCurrent(generation, source)) return null;

    this.clearTimers();
    source.close();
    this.source = null;
    this.readyResources = null;
    this.generation += 1;

    return this.generation;
  }

  private isCurrent(generation: number, source: EventSource) {
    return this.generation === generation && this.source === source;
  }

  private parse<T>(
    schema: {
      safeParse(value: unknown): {success: true; data: T} | {success: false};
    },
    data: string,
  ) {
    try {
      const input: unknown = JSON.parse(data);
      const result = schema.safeParse(input);

      if (!result.success) return null;

      if (
        input !== null &&
        typeof input === "object" &&
        !Array.isArray(input) &&
        result.data !== null &&
        typeof result.data === "object" &&
        !Array.isArray(result.data)
      ) {
        const output = result.data as object;
        const inputKeys = Object.keys(input);
        const outputKeys = Object.keys(output);

        if (
          inputKeys.length !== outputKeys.length ||
          inputKeys.some((key) => !Object.hasOwn(output, key))
        ) {
          return null;
        }
      }

      return result.data;
    } catch {
      return null;
    }
  }

  private clearReadyTimer() {
    if (this.readyTimer !== null) clearTimeout(this.readyTimer);
    this.readyTimer = null;
  }

  private clearTimers() {
    this.clearReadyTimer();
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }
}
